import moment from "moment";
import { createSelector } from "reselect";
import { filterByDate } from "~/utils/date.jsx";
import { descSortCompare } from "~/utils/utils.jsx";
import { getFilterProjectExpenseConfigOption } from "./selector.configOptions.jsx";
import { getAllTasksByProjectId } from "./selector.tasks.jsx";

export const getExpenses = (state) => state.expenses;
export const getExpenseById = (state, id) => state.expenses[`${id}`];

export const getAllExpenses = createSelector(
  (state) => state.expenses,
  (expenses) => Object.values(expenses)
);

export const getAllExpensesSortedByRecordDate = createSelector(getAllExpenses, (expenses) =>
  expenses.sort((a, b) => {
    let comparator1 = moment(a.record_date).utc();
    let comparator2 = moment(b.record_date).utc();

    if (a.record_date === b.record_date) {
      return a.created_on > b.created_on ? -1 : 1;
    }
    return comparator1 > comparator2 ? -1 : 1;
  })
);

export const getExpensesByParentIdAndParentType = createSelector(
  getAllExpensesSortedByRecordDate,
  (state, parentType) => parentType,
  (state, parentType, parentId) => parentId,
  (expenses, parentType, parentId) =>
    expenses.filter(
      (record) => record.parent_type === parentType && record.parent_id === parentId && !record.is_trashed
    )
);

export const getPaginatedExpensesByParentIdAndParentType = createSelector(
  getAllExpensesSortedByRecordDate,
  (state, parentType) => parentType,
  (state, parentType, parentId) => parentId,
  (state, parentType, parentId, cursorDate) => cursorDate,
  (expenses, parentType, parentId, cursorDate) =>
    expenses.filter((expense) => {
      if (cursorDate && expense.record_date <= cursorDate) {
        return false;
      }
      if (expense.is_trashed) {
        return false;
      }
      return expense.parent_type === parentType && expense.parent_id === parentId;
    })
);

export const calculateAllExpensesForParentTypeAndId = createSelector(getExpensesByParentIdAndParentType, (expenses) => {
  let total_expense = 0;
  expenses.forEach((expense) => (total_expense += parseFloat(expense.value)));
  return parseFloat(total_expense).toFixed(2);
});

export const getFilteredProjectExpenses = createSelector(
  getAllExpenses,
  (state, projectId) => projectId,
  (state, projectId, minDate) => minDate,
  getFilterProjectExpenseConfigOption,
  getAllTasksByProjectId,
  (expenses, projectId, minDate, allFilters, tasks) => {
    const filters = allFilters[projectId] ?? {};
    return expenses
      .filter((expense) => {
        if (expense.project_id !== projectId || expense.is_trashed) {
          return false;
        }

        if (minDate && expense.record_date <= minDate) {
          return false;
        }

        if (filters.status && !filters.status.includes(expense.billable_status)) {
          return false;
        }

        if (filters.location && !filters.location.includes(expense.parent_type)) {
          return false;
        }

        if (filters.category && !filters.category.includes(expense.category_id)) {
          return false;
        }

        if (filters.user && !filters.user.includes(expense.user_id)) {
          return false;
        }

        if (filters.date && !filterByDate(expense.record_date, filters.date)) {
          return false;
        }

        if (expense.parent_type === "Task" && !tasks.get(`${expense.parent_id}`)) {
          return false;
        }

        return true;
      })
      .sort((a, b) => {
        if (a.record_date === b.record_date) {
          return a.id > b.id ? -1 : 1;
        } else {
          return descSortCompare(a.record_date, b.record_date);
        }
      });
  }
);

export const getExpensesSumByIds = createSelector(
  getAllExpenses,
  (state, ids) => ids,
  (expenses, ids) => {
    const selected = expenses.filter((item) => ids.includes(item.id));
    const sum = selected.reduce((sum, record) => {
      return sum + record.value;
    }, 0);
    return parseFloat(sum.toFixed(2));
  }
);
